<template>
    <div>
        <div v-if="isHistorical" class="mb-4 flex items-center gap-2 rounded-md bg-amber-50 border border-amber-100 px-3 py-2">
            <UIcon name="i-ph-info-duotone" class="size-4 text-amber-500 flex-shrink-0" />
            <span class="text-xs text-amber-700">Partial history — limited to recorded fields. This call predates full event tracking.</span>
        </div>

        <div v-if="!rows.length" class="h-96 flex flex-col space-y-1 items-center justify-center p-4 pt-6">
            <UIcon name="i-ph-clock-counter-clockwise-duotone" class="size-5 text-gray-500" />
            <span class="text-gray-500 text-[13px] italic">No timeline events.</span>
        </div>

        <ol v-else role="list">
            <li v-for="(row, idx) in rows" :key="row.id" class="flex gap-3">
                <!-- left rail: date over time -->
                <div class="w-16 flex-shrink-0 pt-1 text-right leading-tight" :title="row.absExact">
                    <div class="text-xs font-semibold text-gray-600">{{ row.dateLabel }}</div>
                    <div class="text-[11px] text-gray-400 tabular-nums">{{ row.timeLabel }}</div>
                </div>

                <!-- node + connector -->
                <div class="flex flex-shrink-0 flex-col items-center">
                    <span
                        class="flex size-7 items-center justify-center rounded-full ring-4 ring-white"
                        :class="row.bg"
                    >
                        <UIcon :name="row.icon" class="size-4" :class="row.fg" />
                    </span>
                    <span v-if="idx !== rows.length - 1" class="my-1 w-px flex-1 bg-gray-200" />
                </div>

                <!-- content -->
                <div class="min-w-0 flex-1 pb-6">
                    <div class="flex items-start justify-between gap-2">
                        <p class="text-sm font-semibold text-gray-800 leading-7">{{ row.title }}</p>
                        <span
                            v-if="row.rel"
                            class="mt-1 flex-shrink-0 rounded-full bg-gray-50 px-2 py-0.5 text-[10px] font-medium text-gray-400 ring-1 ring-inset ring-gray-200 tabular-nums"
                            :title="`${row.rel} after call placed`"
                        >+{{ row.rel }}</span>
                    </div>
                    <p v-if="row.subtitle" class="-mt-0.5 text-xs text-gray-500">{{ row.subtitle }}</p>
                    <UBadge
                        v-if="row.duration"
                        :label="row.duration"
                        color="gray"
                        variant="subtle"
                        size="xs"
                        class="mt-1.5"
                    />
                </div>
            </li>
        </ol>
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import type { Call } from '~/types/call'
import { CallEventType, type CallEvent } from '~/types/callEvent'

const props = defineProps<{ events?: CallEvent[]; call: Call }>()

type SynthEvent = Pick<CallEvent, 'id' | 'type' | 'metadata' | 'actor' | 'target' | 'participant' | 'created_at'>

// Real events when present; otherwise synthesize anchor events from the call row.
const sourceEvents = computed<SynthEvent[]>(() => {
    if (props.events?.length) {
        return [...props.events].sort((a, b) => dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf())
    }
    const c = props.call
    const synth: SynthEvent[] = []
    if (c.created_at) synth.push({ id: 'syn-placed', type: CallEventType.callPlaced, metadata: { source: c.source }, created_at: c.created_at })
    if (c.connected_at) synth.push({ id: 'syn-connected', type: CallEventType.operatorConnected, actor: c.user, created_at: c.connected_at })
    if (c.on_hold_at) synth.push({ id: 'syn-hold', type: CallEventType.holdStarted, metadata: { priority: c.metadata?.priority }, created_at: c.on_hold_at })
    if (c.ended_at) {
        if (c.status === 'timed_out') synth.push({ id: 'syn-ended', type: CallEventType.callTimedOut, created_at: c.ended_at })
        else synth.push({ id: 'syn-ended', type: CallEventType.callEnded, metadata: { outcome: c.status }, created_at: c.ended_at })
    }
    return synth
})

const isHistorical = computed(() => !props.events?.length && sourceEvents.value.length > 0)

const PRESENTATION: Record<string, { icon: string; fg: string; bg: string }> = {
    call_placed:         { icon: 'i-ph-phone-incoming-duotone',           fg: 'text-blue-600',   bg: 'bg-blue-100' },
    operator_connected:  { icon: 'i-ph-phone-call-duotone',               fg: 'text-blue-600',   bg: 'bg-blue-100' },
    hold_started:        { icon: 'i-ph-pause-circle-duotone',             fg: 'text-amber-600',  bg: 'bg-amber-100' },
    hold_ended:          { icon: 'i-ph-play-circle-duotone',              fg: 'text-amber-600',  bg: 'bg-amber-100' },
    transfer:            { icon: 'i-ph-arrows-left-right-duotone',        fg: 'text-purple-600', bg: 'bg-purple-100' },
    customer_switched:   { icon: 'i-ph-user-switch-duotone',              fg: 'text-indigo-600', bg: 'bg-indigo-100' },
    participant_joined:  { icon: 'i-ph-user-plus-duotone',                fg: 'text-green-600',  bg: 'bg-green-100' },
    participant_left:    { icon: 'i-ph-user-minus-duotone',               fg: 'text-gray-500',   bg: 'bg-gray-100' },
    guest_invited:       { icon: 'i-ph-share-network-duotone',            fg: 'text-teal-600',   bg: 'bg-teal-100' },
    video_upgraded:      { icon: 'i-ph-video-camera-duotone',             fg: 'text-teal-600',   bg: 'bg-teal-100' },
    email_sent:          { icon: 'i-ph-envelope-simple-duotone',          fg: 'text-teal-600',   bg: 'bg-teal-100' },
    message_sent:        { icon: 'i-ph-chat-circle-text-duotone',         fg: 'text-teal-600',   bg: 'bg-teal-100' },
    report_filed:        { icon: 'i-ph-file-text-duotone',                fg: 'text-slate-600',  bg: 'bg-slate-100' },
    recording_generated: { icon: 'i-ph-cassette-tape-duotone',            fg: 'text-slate-600',  bg: 'bg-slate-100' },
    call_ended:          { icon: 'i-ph-phone-x-duotone',                  fg: 'text-green-600',  bg: 'bg-green-100' },
    call_timed_out:      { icon: 'i-ph-clock-countdown-duotone',          fg: 'text-red-600',    bg: 'bg-red-100' },
    feedback_received:   { icon: 'i-ph-star-duotone',                     fg: 'text-yellow-600', bg: 'bg-yellow-100' },
}

const TITLES: Record<string, string> = {
    call_placed: 'Call placed',
    operator_connected: 'Operator connected',
    hold_started: 'Placed on hold',
    hold_ended: 'Resumed from hold',
    transfer: 'Transferred',
    customer_switched: 'Customer switched',
    participant_joined: 'Joined',
    participant_left: 'Left',
    guest_invited: 'Guest invited',
    video_upgraded: 'Upgraded to video',
    email_sent: 'Email sent',
    message_sent: 'Message sent',
    report_filed: 'Report filed',
    recording_generated: 'Recording generated',
    call_ended: 'Call ended',
    call_timed_out: 'Call timed out',
    feedback_received: 'Feedback received',
}

function formatOffset(ms: number): string {
    const s = Math.max(0, Math.round(ms / 1000))
    if (s < 60) return `${s}s`
    const m = Math.floor(s / 60)
    const rem = s % 60
    if (m < 60) return rem ? `${m}m ${rem}s` : `${m}m`
    const h = Math.floor(m / 60)
    return `${h}h ${m % 60}m`
}

function subtitleFor(e: SynthEvent): string {
    const m = e.metadata ?? {}
    switch (e.type) {
        case CallEventType.transfer: return `${e.actor?.name ?? 'Operator'} → ${e.target?.name ?? 'Operator'}`
        case CallEventType.customerSwitched: return `${m.from || 'Unknown'} → ${m.to || 'Unknown'}`
        case CallEventType.holdStarted: return m.priority ? `Priority ${m.priority}` : ''
        case CallEventType.participantJoined:
        case CallEventType.participantLeft: return [e.participant, m.identity].filter(Boolean).join(' · ')
        case CallEventType.guestInvited: return m.recipient ? `WhatsApp → ${m.recipient}` : 'WhatsApp'
        case CallEventType.emailSent: return [m.kind, m.recipient].filter(Boolean).join(' · ')
        case CallEventType.messageSent: return [m.kind, m.channel].filter(Boolean).join(' · ')
        case CallEventType.reportFiled: return m.report_number ? `Report #${m.report_number}` : ''
        case CallEventType.callEnded: return m.outcome ? String(m.outcome).replaceAll('_', ' ') : ''
        case CallEventType.feedbackReceived: return m.rating ? `${m.rating}/5` : ''
        default: return e.actor?.name ?? ''
    }
}

const rows = computed(() => {
    const evts = sourceEvents.value
    if (!evts.length) return []
    const anchor = dayjs(evts[0].created_at)

    // Duration pairing: hold stack + participant-by-identity map + connect/end vs placed.
    let lastHold: dayjs.Dayjs | null = null
    const joinByIdentity = new Map<string, dayjs.Dayjs>()
    const participantKey = (e: SynthEvent) => e.participant ?? e.metadata?.identity ?? ''

    return evts.map((e) => {
        const at = dayjs(e.created_at)
        const pres = PRESENTATION[e.type] ?? { icon: 'i-ph-circle-duotone', fg: 'text-gray-500', bg: 'bg-gray-100' }
        let duration = ''

        if (e.type === CallEventType.operatorConnected) {
            duration = `TTA ${formatOffset(at.diff(anchor))}`
        } else if (e.type === CallEventType.holdStarted) {
            lastHold = at
        } else if (e.type === CallEventType.holdEnded && lastHold) {
            duration = `Held ${formatOffset(at.diff(lastHold))}`
            lastHold = null
        } else if (e.type === CallEventType.participantJoined) {
            const key = participantKey(e)
            if (key) joinByIdentity.set(key, at)
        } else if (e.type === CallEventType.participantLeft) {
            const key = participantKey(e)
            const start = key ? joinByIdentity.get(key) : undefined
            if (start) { duration = `On call ${formatOffset(at.diff(start))}`; joinByIdentity.delete(key) }
        } else if (e.type === CallEventType.callEnded || e.type === CallEventType.callTimedOut) {
            duration = `Total ${formatOffset(at.diff(anchor))}`
        }

        const offsetMs = at.diff(anchor)

        return {
            id: e.id,
            icon: pres.icon,
            fg: pres.fg,
            bg: pres.bg,
            title: TITLES[e.type] ?? e.type,
            subtitle: subtitleFor(e),
            dateLabel: at.format('MMM D'),
            timeLabel: at.format('h:mm A'),
            absExact: at.format('MMM D, YYYY · h:mm:ss A'),
            rel: offsetMs > 0 ? formatOffset(offsetMs) : '',
            duration,
        }
    })
})
</script>
